import React, { useState, useEffect } from 'react';
import { formatCurrency as formatPrice } from '../../lib/format';
import {
  ShoppingCart,
  X,
  Plus,
  Minus,
  Trash2,
  User,
  ShoppingBag,
  CheckCircle
} from 'lucide-react';
import { useCartStore } from '../../store/useCartStore';
import { apiClient } from '../../lib/api';
import { OrderPharmacyBadge } from '../pharmacy/PharmacyBadge';
import { AsyncSearchSelect } from '../common/AsyncSearchSelect';

interface Patient {
  _id: string;
  firstName: string;
  lastName: string;
  phone: string;
  email?: string;
  status: 'active' | 'inactive';
}

interface ShoppingCartProps {
  className?: string;
}

export const ShoppingCartComponent: React.FC<ShoppingCartProps> = ({ className = '' }) => {
  const {
    items,
    selectedPatient,
    isOpen,
    totalItems,
    subtotal,
    shippingAmount,
    shippingLoading,
    totalAmount,
    updateQuantity,
    removeItem,
    clearCart,
    closeCart,
    canCreateOrder,
    getOrderData,
    setSelectedPatient,
    setShippingAmount,
    setShippingLoading
  } = useCartStore();
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  
  // Fetch shipping rate when cart opens
  useEffect(() => {
    if (isOpen && items.length > 0) {
      fetchShippingRate();
    }
  }, [isOpen, items.length]);

  const fetchShippingRate = async () => {
    try {
      setShippingLoading(true);
      const response = await apiClient.getShippingRate();

      if (response.success && response.data) {
        setShippingAmount(response.data.shippingRate);
      }
    } catch (error) {
      console.error('Error fetching shipping rate:', error);
      // Default to 0 if we can't fetch the rate
      setShippingAmount(0);
    } finally {
      setShippingLoading(false);
    }
  };

  const handlePatientSelect = (patient: Patient) => {
    setSelectedPatient({
      id: patient._id,
      firstName: patient.firstName,
      lastName: patient.lastName,
      fullName: `${patient.firstName} ${patient.lastName}`
    });
  };

  const handleUpdateQuantity = (productId: string, newQuantity: number) => {
    updateQuantity(productId, newQuantity);
  };

  const handleRemoveItem = (productId: string) => {
    removeItem(productId);
  };

  const handleCreateOrder = async () => {
    const orderData = getOrderData();
    if (!orderData) {
      setSubmitError('Please select a patient and add items to cart');
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);
    setSubmitSuccess(false);

    try {
      const response = await apiClient.createOrder(orderData);
      
      if (response.success) {
        setSubmitSuccess(true);
        clearCart();
        setTimeout(() => {
          setSubmitSuccess(false);
          closeCart();
        }, 2000);
      } else {
        setSubmitError(response.error || 'Failed to create order');
      }
    } catch (error: any) {
      setSubmitError(error.message || 'An unexpected error occurred');
    } finally {
      setIsSubmitting(false);
    }
  };


  if (!isOpen) return null;

  return (
    <div className={`fixed inset-0 bg-black bg-opacity-50 z-50 ${className}`}>
      <div className="fixed right-0 top-0 h-full w-full max-w-md bg-white shadow-xl">
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b">
            <div className="flex items-center space-x-2">
              <ShoppingCart className="h-6 w-6 text-blue-600" />
              <h2 className="text-lg font-semibold">Shopping Cart</h2>
              {totalItems > 0 && (
                <span className="bg-blue-100 text-blue-600 px-2 py-1 rounded-full text-sm">
                  {totalItems}
                </span>
              )}
            </div>
            <button 
              onClick={closeCart}
              className="text-gray-500 hover:text-gray-700"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          {/* Patient Selection */}
          <div className="p-4 border-b bg-gray-50">
            <div className="relative">
              {selectedPatient ? (
                <div className="flex items-center justify-between bg-green-50 border border-green-200 rounded-lg p-3">
                  <div className="flex items-center space-x-2 text-green-700">
                    <User className="h-5 w-5" />
                    <span className="font-medium">{selectedPatient.fullName}</span>
                  </div>
                  <button
                    onClick={() => setSelectedPatient(null)}
                    className="text-green-600 hover:text-green-800 text-sm"
                  >
                    Change
                  </button>
                </div>
              ) : (
                // Searched on the server: the clinic may have far more patients
                // than one page, and the old dropdown only held the newest 50.
                <AsyncSearchSelect<Patient>
                  fetchPage={({ page, pageSize, search, signal }) =>
                    apiClient.searchPatientsPage({ page, pageSize, search, signal, sortBy: 'name', sortOrder: 'asc' })}
                  getKey={(p) => p._id}
                  getLabel={(p) => `${p.firstName} ${p.lastName}`}
                  getDescription={(p) => p.email || p.phone}
                  value={null}
                  onChange={(p) => { if (p) handlePatientSelect(p); }}
                  placeholder="Search patients by name, phone or email..."
                  noun="patients"
                  aria-label="Select a patient"
                />
              )}
            </div>
          </div>

          {/* Cart Items */}
          <div className="flex-1 overflow-y-auto">
            {items.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-gray-500">
                <ShoppingBag className="h-16 w-16 mb-4 text-gray-300" />
                <p className="text-lg font-medium">Your cart is empty</p>
                <p className="text-sm">Browse products to add items</p>
              </div>
            ) : (
              <div className="p-4 space-y-4">
                {items.map((item) => (
                  <div key={`${item.productId}-${JSON.stringify(item.selectedVariants)}`} 
                       className="bg-white border rounded-lg p-4">
                    <div className="flex justify-between items-start mb-2">
                      <h3 className="font-medium text-gray-900 line-clamp-2">
                        {item.productName}
                      </h3>
                      <button
                        onClick={() => handleRemoveItem(item.productId)}
                        className="text-red-500 hover:text-red-700 ml-2"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                    
                    {item.selectedVariants && Object.keys(item.selectedVariants).length > 0 && (
                      <div className="mb-2">
                        {Object.entries(item.selectedVariants).map(([key, value]) => (
                          <span key={key} className="inline-block bg-gray-100 text-gray-700 px-2 py-1 rounded text-xs mr-1">
                            {key}: {value}
                          </span>
                        ))}
                      </div>
                    )}
                    
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleUpdateQuantity(item.productId, item.quantity - 1)}
                          className="p-1 text-gray-500 hover:text-gray-700"
                        >
                          <Minus className="h-4 w-4" />
                        </button>
                        <span className="w-8 text-center">{item.quantity}</span>
                        <button
                          onClick={() => handleUpdateQuantity(item.productId, item.quantity + 1)}
                          className="p-1 text-gray-500 hover:text-gray-700"
                        >
                          <Plus className="h-4 w-4" />
                        </button>
                      </div>
                      <div className="text-right">
                        <div className="text-sm text-gray-500">
                          {formatPrice(item.unitPrice)} each
                        </div>
                        <div className="font-medium">
                          {formatPrice(item.totalPrice)}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          {items.length > 0 && (
            <div className="border-t p-4 bg-gray-50">
              {/* Fulfillment pharmacy hint — where this order will be filled */}
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm text-gray-500">Filled by</span>
                <OrderPharmacyBadge items={items} size="sm" />
              </div>

              {/* Price breakdown */}
              <div className="space-y-2 mb-4">
                <div className="flex justify-between items-center text-gray-600">
                  <span>Subtotal</span>
                  <span>{formatPrice(subtotal)}</span>
                </div>
                <div className="flex justify-between items-center text-gray-600">
                  <span>Shipping</span>
                  {shippingLoading ? (
                    <span className="text-gray-400">Calculating...</span>
                  ) : (
                    <span>{formatPrice(shippingAmount)}</span>
                  )}
                </div>
                <div className="border-t pt-2 flex justify-between items-center">
                  <span className="text-lg font-semibold">Total</span>
                  <span className="text-xl font-bold text-blue-600">
                    {formatPrice(totalAmount)}
                  </span>
                </div>
              </div>
              
              {submitError && (
                <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
                  {submitError}
                </div>
              )}
              
              {submitSuccess && (
                <div className="mb-4 p-3 bg-green-100 border border-green-400 text-green-700 rounded flex items-center">
                  <CheckCircle className="h-5 w-5 mr-2" />
                  Order created successfully!
                </div>
              )}
              
              <div className="space-y-2">
                <button
                  onClick={handleCreateOrder}
                  disabled={!canCreateOrder() || isSubmitting}
                  className="w-full py-3 px-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center"
                >
                  {isSubmitting ? (
                    <div className="flex items-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Creating Order...
                    </div>
                  ) : (
                    'Create Order'
                  )}
                </button>
                
                <button
                  onClick={clearCart}
                  className="w-full py-2 px-4 text-gray-600 hover:text-gray-800"
                >
                  Clear Cart
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}; 